'use client';

import React, { useEffect } from 'react';
import { Dialog, PulseButton, Typography } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUserSlash, faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { useLogging } from 'src/context/logging';
import {
  SEAT_LIMIT_COPY,
  SEAT_LIMIT_EMAIL_SUBJECT,
  SUPPORT_EMAIL,
} from './constants';

interface SeatLimitDialogProps {
  open: boolean;
  onClose: () => void;
  /** Contracted seats. `0` when the account has no seat count recorded. */
  totalLicenses?: number;
  /** Seats currently held — shown so the figures match the license overview. */
  usedLicenses?: number;
}

/**
 * @description Opens in place of the add-user dialog when every license is in
 *              use (MKT-383). It explains the two ways out — free a seat by
 *              deactivating someone, or ask for more — rather than letting the
 *              admin fill in a form the API would reject.
 * @returns React.ReactElement
 */
const SeatLimitDialog: React.FC<SeatLimitDialogProps> = ({
  open,
  onClose,
  totalLicenses = 0,
  usedLicenses = 0,
}) => {
  const { event: logEvent } = useLogging();

  useEffect(() => {
    if (!open) return;

    logEvent('Seat Limit Reached', {
      source: 'SeatLimitDialog',
      usedLicenses,
      totalLicenses,
    });
  }, [open, logEvent, usedLicenses, totalLicenses]);

  // `window.open(..., '_self')` rather than assigning `location.href`: the mail
  // client takes over either way, but this keeps the handoff out of the history
  // entry and is straightforward to assert in tests.
  const handleEmailSupport = () => {
    window.open(
      `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        SEAT_LIMIT_EMAIL_SUBJECT
      )}`,
      '_self'
    );
  };

  return (
    <Dialog
      size="lg"
      isOpen={open}
      onClose={onClose}
      showCloseButton
      className="!h-auto"
      aria-labelledby="seat-limit-dialog-title"
      aria-describedby="seat-limit-dialog-description"
      data-testid="seat-limit-dialog"
    >
      <Dialog.Header>
        {/* The id lives on the wrapper: Typography takes no id of its own. */}
        <div id="seat-limit-dialog-title" data-testid="seat-limit-dialog-title">
          <Typography variant="h5">{SEAT_LIMIT_COPY.title}</Typography>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Icon icon={faUserSlash} className="text-amber-600" />
            </div>
            <p
              id="seat-limit-dialog-description"
              className="text-sm text-neutral-700"
              data-testid="seat-limit-dialog-summary"
            >
              {totalLicenses > 0
                ? SEAT_LIMIT_COPY.summary(totalLicenses)
                : SEAT_LIMIT_COPY.summaryNoLimit}
            </p>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <p className="mb-2 text-sm font-semibold text-neutral-800">
              {SEAT_LIMIT_COPY.optionsTitle}
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-700">
              <li data-testid="seat-limit-dialog-deactivate-option">
                {SEAT_LIMIT_COPY.deactivateOption}
              </li>
              <li data-testid="seat-limit-dialog-contact-option">
                {SEAT_LIMIT_COPY.contactOption}
              </li>
            </ul>
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex flex-row gap-2">
          <PulseButton
            variant="outline"
            color="neutral"
            onClick={onClose}
            aria-label={SEAT_LIMIT_COPY.closeAction}
            data-testid="seat-limit-dialog-close"
          >
            {SEAT_LIMIT_COPY.closeAction}
          </PulseButton>
          <PulseButton
            variant="solid"
            color="primary"
            startDecorator={faEnvelope}
            onClick={handleEmailSupport}
            aria-label={`${SEAT_LIMIT_COPY.emailAction} at ${SUPPORT_EMAIL}`}
            data-testid="seat-limit-dialog-email"
          >
            {SEAT_LIMIT_COPY.emailAction}
          </PulseButton>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default SeatLimitDialog;
